<template>
  <mailbox-settings-layout :name="settings.name">
    <el-card class="mailbox-settings-conversation-rules">
      <template #header>
        <h3>Conversation Rules</h3>
      </template>
      <entity-settings-loading v-if="isLoading" />
      <template v-else>
        <el-form
          ref="mailboxForm"
          :model="formData"
          :rules="rules"
          label-position="top"
          class="mailbox-settings-conversation-rules__form"
        >
          <div>
            <h4>Initial Conversation Settings</h4>
            <p>Set a conversation’s settings when an incoming email arrives in this mailbox.</p>
            <p><br></p>
            <div class="row">
              <div class="col-lg-4">
                <el-form-item label="Status" prop="defaultStatusID">
                  <single-select v-model="formData.defaultStatusID" :disabled="isSubmitting" placeholder="Select your status">
                    <el-option v-for="item in settings.listStatuses" :key="item.id" :value="item.id" :label="item.name" />
                  </single-select>
                </el-form-item>
              </div>
              <div class="col-lg-4">
                <el-form-item label="Priority" prop="defaultPriorityID">
                  <single-select v-model="formData.defaultPriorityID" :disabled="isSubmitting" placeholder="Select your priority">
                    <el-option v-for="item in settings.listPriorities" :key="item.id" :value="item.id" :label="item.name" />
                  </single-select>
                </el-form-item>
              </div>
              <div class="col-lg-4">
                <el-form-item label="Tags" prop="listDefaultTags">
                  <tags-input 
                    v-model:selected-tags="formData.listDefaultTags" 
                    hide-icon
                    disable-selection
                    show-input-border
                  />
                </el-form-item>
              </div>
              <!-- Jobs: Default job appointees -->
              <template v-if="settings.hasJobsEnabled">
                <div
                  v-for="(defaultJobAppointee, index) in settings.listDefaultJobAppointees" 
                  :key="index" 
                  class="col-lg-4"
                >
                  <el-form-item :label="defaultJobAppointee.job.name">
                    <single-select v-model="formData.listDefaultJobAppointeeMappings[index].appointeeID" :disabled="isSubmitting" placeholder="Select person">
                      <el-option v-for="item in settings.listAssignees" :key="item.id" :value="item.id" :label="item.name" />
                    </single-select>
                  </el-form-item>
                </div>
              </template>
              <!-- No jobs: Default assignee -->
              <div v-else class="col-lg-4">
                <el-form-item label="Assignee" prop="defaultAssigneeID">
                  <single-select v-model="formData.defaultAssigneeID" :disabled="isSubmitting" placeholder="Select your assignee">
                    <el-option v-for="item in settings.listAssignees" :key="item.id" :value="item.id" :label="item.name" />
                  </single-select>
                </el-form-item>
              </div>
            </div>
          </div>
          <div>
            <h5>Spam Filtering</h5>
            <div class="spam-check-row">
              <el-switch v-model="enableSpamCheck" />
              <p v-if="enableSpamCheck">
                Send any email detected as potentially malicious to the spam filter.
              </p>
              <p v-else>
                Never send incoming email to the spam filter.
              </p>
            </div>
          </div>
          <div class="dd-divider" />
          <div>
            <h4>Workflow Settings</h4>
            <p>
              When a customer replies to an issue that is
              <strong
                class="mailbox-settings-conversation-rules__strong"
              >inactive…</strong>
            </p>
            <p><br></p>
            <div class="row">
              <div class="col-lg-6 col-xl-6">
                <el-form-item prop="keepStatus">
                  <el-radio-group
                    v-model="keepStatus"
                    :disabled="isSubmitting"
                  >
                    <el-radio :value="false">Keep the status the same</el-radio>
                    <el-radio :value="true">Automatically update the status to...</el-radio>
                  </el-radio-group>
                </el-form-item>
              </div>
              <div class="col-lg-6 col-xl-6 mailbox-settings-conversation-rules__form-select">
                <el-form-item prop="inactiveUpdateStatusID">
                  <single-select v-model="formData.inactiveUpdateStatusID" :disabled="isSubmitting" placeholder="Select your status">
                    <el-option v-for="item in settings.listStatuses" :key="item.id" :value="item.id" :label="item.name" />
                  </single-select>
                </el-form-item>
              </div>
            </div>
          </div>
        </el-form>
        <dd-button
          :is-loading="isSubmitting"
          :color="ColorAssistant.MailboxColor"
          class="float-right"
          size="large"
          @click="validateToSave"
        >Save Changes</dd-button>
        <div class="clearfix" />
      </template>
    </el-card>
  </mailbox-settings-layout>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import MailboxSettingsLayout from '@/layouts/mailbox-settings-layout.vue'

/* Mixins */

/* Components */
import EntitySettingsLoading from '@/layouts/entity-settings-loading.vue'

/* Helpers */
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import { IMailboxesOutputIncomingEmailSettings } from '@/api/types/response.types'

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'
import { IMailboxesInputIncomingEmailSettings } from '@/api/types/request.types'

export default defineComponent({
  name: 'MailboxSettingsConversationRules',

  components: {
    MailboxSettingsLayout,
    EntitySettingsLoading,
  },

  data() {
    return {
      ColorAssistant,
      isLoading: true,
      isSubmitting: false,
      enableSpamCheck: true,
      keepStatus: false,
      settings: {
        name: '',
      } as IMailboxesOutputIncomingEmailSettings,
      formData: {} as IMailboxesInputIncomingEmailSettings,
      rules: {
        inactiveUpdateStatusID: [
          {
            validator: (rule: any, value: any, callback: any) => {
              // @ts-ignore
              if (this.keepStatus && !value) {
                callback(new Error('Select a status'))
              } else {
                callback()
              }
            },
            trigger: 'change',
          },
        ],
      },
    }
  },

  head(): any {
    return {
      title: `Conversation Rules - ${this.settings.name}`,
    }
  },

  computed: {
    id(): number {
      return parseInt(this.$route.params.id, 10)
    },
  },

  watch: {
    'formData.inactiveUpdateStatusID'(value): void {
      if (value !== null) {
        this.keepStatus = true
      }
    },
    keepStatus(value): void {
      if (value === false) {
        this.formData.inactiveUpdateStatusID = null
        // @ts-ignore
        this.$refs.mailboxForm.clearValidate(['inactiveUpdateStatusID'])
      }
    },
  },

  async created() {
    this.isLoading = true
    await this.getConversationRulesInfo()
    this.isLoading = false
  },

  methods: {
    async getConversationRulesInfo() {
      try {
        this.settings = await MailboxesAPI.get_IncomingEmailSettings(this.id)
        this.enableSpamCheck = !this.settings.bypassSpamCheck
        this.formData = {
          defaultAssigneeID: this.settings.defaultAssigneeID,
          defaultStatusID: this.settings.defaultStatusID,
          defaultPriorityID: this.settings.defaultPriorityID,
          listDefaultTags: this.settings.listDefaultTags,
          listDefaultJobAppointeeMappings: this.settings.listDefaultJobAppointees.map(
            dja => ({
              jobID: dja.job.id,
              appointeeID: dja.appointee ? dja.appointee.id : 0,
            }),
          ),
          inactiveUpdateStatusID: this.settings.inactiveUpdateStatusID,
          bypassSpamCheck: this.settings.bypassSpamCheck,
        } as IMailboxesInputIncomingEmailSettings
      } catch (error) {
        this.$error.emit(error)
      }
    },

    /**
     * Validate all attributes before save
     */
    validateToSave(): boolean {
      // @ts-ignore
      this.$refs.mailboxForm.validate((valid: boolean) => {
        if (valid) {
          this.save()
          return true
        }
      })
      return false
    },

    /**
     * Save new data
     */
    async save(): Promise<void> {
      try {
        this.isSubmitting = true
        this.formData.bypassSpamCheck = !this.enableSpamCheck
        const response = await MailboxesAPI.post_IncomingEmailSettings(
          this.id,
          this.formData,
        )
        this.$messaging.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
        await this.getConversationRulesInfo()
      } finally {
        this.isSubmitting = false
      }
    },
  },
})
</script>

<style lang="scss" scoped>
.mailbox-settings-conversation-rules {
  overflow: visible;

  .el-radio-group {
    padding-top: 0;
  }

  .spam-check-row {
    display: flex;
    align-items: center;

    > * {
      margin-right: 10px;
    }

    p {
      @include lettering--p-sm;
    }
  }
}
</style>
